// src/services/contacts.js

import api from '../api/axios';

/**
 * Puoi cambiare BASE_URL se il tuo backend risponde
 * su un altro dominio o porta (es. http://localhost:8080).
 */
const BASE_URL = 'api/contacts';

/**
 * I DocumentNode vivono ora sotto `/api/documents/nodes/` (unificazione documentale, F7).
 * Il payload di create resta invariato: il client continua a mandare `contact`
 * (campo compat write-only lato serializer). La migrazione a owner_type/owner_id
 * è opzionale e fuori scope.
 */
const DOCS_URL = 'api/documents';

/**
 * Ottiene la lista dei contatti
 */
export const getContacts = (params = {}) => {
  return api.get(`${BASE_URL}/contacts/`, { params });
};

/**
 * Ottiene un contatto specifico per ID
 */
export const getContact = (id) => {
  return api.get(`${BASE_URL}/contacts/${id}/`);
};

/**
 * Crea un nuovo contatto (POST /api/contacts/)
 */
export function createContact(data) {
  return api.post(`${BASE_URL}/contacts/`, data);
}

/**
 * Aggiorna interamente un contatto (PUT /api/contacts/<id>/)
 */
export function updateContact(id, data) {
  return api.put(`${BASE_URL}/contacts/${id}/`, data);
}

/**
 * Aggiorna parzialmente un contatto (PATCH /api/contacts/<id>/)
 * (non sempre serve, dipende dalla tua logica)
 */
export function patchContact(id, data) {
  return api.patch(`${BASE_URL}/contacts/${id}/`, data);
}

/**
 * Elimina un contatto (DELETE /api/contacts/<id>/)
 */
export function deleteContact(id) {
  return api.delete(`${BASE_URL}/contacts/${id}/`);
}

export const getContactActivityFiles = (id, offset = 0) =>
  api.get(`${BASE_URL}/contacts/${id}/activity-files/`, { params: { limit: 100, offset } });

// Funzioni per gestire gli allegati dei contatti (DocumentNode)
export const uploadContactAttachment = (contactId, fileData) => {
  const formData = new FormData();
  formData.append('file', fileData);
  formData.append('contact', contactId);
  formData.append('node_type', 'FILE');
  formData.append('name', fileData.name);

  return api.post(`${DOCS_URL}/nodes/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const getContactAttachments = (contactId) => {
  return api.get(`${DOCS_URL}/nodes/`, { params: { contact_id: contactId } });
};

export const getDocumentNode = (id) =>
  api.get(`${DOCS_URL}/nodes/${id}/`);

export const deleteContactAttachment = (attachmentId) => {
  return api.delete(`${DOCS_URL}/nodes/${attachmentId}/`);
};

// --- DocumentNode CRUD per il file-manager Cubone ---

export const createDocumentFolder = (contactId, name, parentId = null) => {
  const fd = new FormData();
  fd.append('contact', contactId);
  fd.append('node_type', 'FOLDER');
  fd.append('name', name);
  if (parentId !== null) fd.append('parent', parentId);
  return api.post(`${DOCS_URL}/nodes/`, fd);
};

export const uploadDocumentFile = (contactId, fileData, parentId = null) => {
  const fd = new FormData();
  fd.append('file', fileData);
  fd.append('contact', contactId);
  fd.append('node_type', 'FILE');
  fd.append('name', fileData.name);
  if (parentId !== null) fd.append('parent', parentId);
  return api.post(`${DOCS_URL}/nodes/`, fd);
};

export const renameDocumentNode = (id, newName) =>
  api.patch(`${DOCS_URL}/nodes/${id}/`, { name: newName });

export const moveDocumentNode = (id, newParentId) =>
  api.patch(`${DOCS_URL}/nodes/${id}/`, { parent: newParentId });

export const deleteDocumentNode = (id) =>
  api.delete(`${DOCS_URL}/nodes/${id}/`);

export const downloadDocumentFile = (id) =>
  api.get(`${DOCS_URL}/nodes/${id}/download/`, { responseType: 'blob' });

// POST {node_ids: [...]} → blob zip. Backend valida cap, dedup
// parent/descendant, e ritorna 400/404/500 con body JSON in caso di errore.
export const zipDocumentNodes = (nodeIds) =>
  api.post(
    `${DOCS_URL}/nodes/zip/`,
    { node_ids: nodeIds },
    { responseType: 'blob' }
  );

// Funzioni per gestire gli allegati temporanei
export const uploadTempContactAttachment = (fileData, sessionKey = null) => {
  const formData = new FormData();
  formData.append('file', fileData);
  if (sessionKey) {
    formData.append('session_key', sessionKey);
  }
  
  return api.post(`${BASE_URL}/contacts/upload_temp_attachment/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const getTempContactAttachments = (sessionKey) => {
  return api.get(`${BASE_URL}/contacts/get_temp_attachments/`, { 
    params: { session_key: sessionKey } 
  });
};

export const deleteTempContactAttachment = (attachmentId) => {
  return api.delete(`${BASE_URL}/contacts/delete_temp_attachment/`, { 
    params: { attachment_id: attachmentId } 
  });
};

export const cleanupTempContactAttachments = (sessionKey) => {
  return api.delete(`${BASE_URL}/contacts/cleanup_temp_attachments/`, {
    params: { session_key: sessionKey }
  });
};

// Funzioni per gestire i tag dei contatti
export const getTags = () => {
  return api.get(`${BASE_URL}/tags/`);
};

export const createTag = (data) => {
  return api.post(`${BASE_URL}/tags/`, data);
};

export const deleteTag = (tagId) => {
  return api.delete(`${BASE_URL}/tags/${tagId}/`);
};
